import { ArrowLeftIcon } from "@heroicons/react/outline";
import { LoadingModal } from "Component/LoadingModal";
import { Notify } from "Component/Notify";
import { fetchApi } from "Config/fetchApi";
import React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "Route/ConstantRoute";
import { EncryptStorage } from "encrypt-storage";
import { handleError, isoStringToDateInput } from "Helper";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

function UpdateVehicleInventory() {
  const encryptStorage = new EncryptStorage(process.env.REACT_APP_SECRET_KEY);
  const userInfo = encryptStorage.getItem("___info");
  const yuridiksi_user_code = userInfo?.jurisdiction_level_code || "";
  const navigate = useNavigate();
  const location = useLocation();
  const argumentData = location.state;
  const [uuid] = useState(argumentData?.id || "");
  const [isLoading, setIsLoading] = useState(false);
  const [vehicleName, setVehicleName] = useState(
    argumentData?.nama_kendaraan || ""
  );
  const [vehicleType, setVehicleType] = useState(
    argumentData?.jenis_kendaraan || ""
  );
  const [vehicleModel, setVehicleModel] = useState(
    argumentData?.tipe_model || ""
  );
  const [registeredNumber, setRegisteredNumber] = useState(
    argumentData?.nomer_registrasi || ""
  );
  const [bpkb, setBpkb] = useState(argumentData?.nomor_bpkb || "");
  const [machineNumber, setMachineNumber] = useState(
    argumentData?.nomor_mesin || ""
  );
  const [manufacturedYear, setManufacturedYear] = useState(
    isoStringToDateInput(argumentData?.tahun_pembuatan) || ""
  );
  const [cilinder, setCilinder] = useState(argumentData?.silinder || "");
  const [cameraId, setCameraId] = useState(
    argumentData?.inventory_camera_id || ""
  );
  const [dataVehicle, setDataVehicle] = useState([]);
  const [dataDeviceType, setDataDeviceType] = useState([]);
  const [dataCamera, setDataCamera] = useState([]);

  // ==================== Default ==================== //
  const goBack = () => {
    navigate(ROUTES.inventory.vehicle.vehicle_home);
  };

  const handleSuccess = (message) => {
    Notify.success({
      title: "Berhasil",
      description: message,
    });
  };

  const getDataVehicle = async () => {
    try {
      setIsLoading(true);
      const response = await fetchApi().get(`/inventory-kendaraan/get-list`);
      const filterFlagData = response.data?.data.filter(
        (item) => item.flag_active_operation_status === true
      );

      setDataVehicle(filterFlagData);
      setIsLoading(false);
    } catch (err) {
      setDataVehicle([]);
      setIsLoading(false);
      handleError(err);
    }
  };

  const getDataDeviceType = async () => {
    try {
      const response = await fetchApi().get(`/device-type/get-list`);
      const filterFlagData = response.data?.data.filter(
        (item) => item.flag_active_device_type === true
      );
      setDataDeviceType(filterFlagData);
      setIsLoading(false);
    } catch (err) {
      setDataDeviceType([]);
      setIsLoading(false);
      handleError(err);
    }
  };

  const getCameraList = async () => {
    try {
      const response = await fetchApi().get(`/inventory-camera/get-list`);
      setDataCamera(response.data?.data || []);
    } catch (err) {
      setDataCamera([]);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };
  // ==================== Default ==================== //

  const updateData = async () => {
    try {
      setIsLoading(true);

      let reqBody;
      if (vehicleType !== "MOTOR") {
        reqBody = {
          nama_kendaraan: vehicleName,
          jenis_kendaraan: vehicleType,
          tipe_model: vehicleModel,
          nomer_registrasi: registeredNumber,
          nomor_bpkb: bpkb,
          nomor_mesin: machineNumber,
          tahun_pembuatan: manufacturedYear,
          silinder: cilinder,
        };
      } else {
        reqBody = {
          nama_kendaraan: vehicleName,
          jenis_kendaraan: vehicleType,
          tipe_model: vehicleModel,
          nomer_registrasi: registeredNumber,
          nomor_bpkb: bpkb,
          nomor_mesin: machineNumber,
          tahun_pembuatan: manufacturedYear,
          silinder: cilinder,
          inventory_camera_id: cameraId,
        };
      }

      const response = await fetchApi().put(
        `/inventory-kendaraan/update/${uuid}`,
        reqBody
      );
      setIsLoading(false);
      handleSuccess(`Data ${vehicleName} berhasil diperbarui`);
      goBack();
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateData();
  };

  useEffect(() => {
    getDataVehicle();
    getDataDeviceType();
    getCameraList();
  }, []);

  return (
    <div>
      <div className="text-lg font-bold text-start gap-2 pb-5">
        <p
          className="text-2xl font-bold"
          style={{
            fontFamily: "Poppins",
          }}
        >
          Ubah Inventaris Kendaraan
        </p>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="rounded-2xl border-solid border-gray-300 border shadow-xl bg-white p-6">
          <div className="flex justify-between items-center px-5 w-full">
            <div className="w-2/4">
              <button
                type="submit"
                onClick={goBack}
                disabled={isLoading}
                className="bg-zinc-200 h-11 flex w-auto justify-center items-center rounded-xl hover:bg-gray-100 px-3 py-1.5 text-md font-semibold leading-6 text-black focus-visible:outline shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <ArrowLeftIcon className="h-6 w-auto pr-2" />
                Kembali
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-6 p-5">
            {/* Nama Kendaraan */}
            <div>
              <label
                htmlFor="nama_kendaraan"
                className="flex text-sm font-medium leading-6"
              >
                Nama Kendaraan <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <input
                  id="nama_kendaraan"
                  name="nama_kendaraan"
                  type="text"
                  disabled={isLoading}
                  value={vehicleName}
                  placeholder="Masukkan Nama Kendaraan"
                  onChange={(e) => setVehicleName(e.target.value)}
                  required
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Jenis Kendaraan */}
            <div>
              <label
                htmlFor="jenis_kendaraan"
                className="flex text-sm font-medium leading-6"
              >
                Jenis Kendaraan <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <select
                  id="jenis_kendaraan"
                  name="jenis_kendaraan"
                  disabled={isLoading}
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  required
                  className={`font-[Poppins] h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 focus:ring-2 focus:ring-blue-500 sm:text-sm ${
                    vehicleType === "" ? "text-gray-400 " : "text-gray-700"
                  }`}
                >
                  <option value="" disabled hidden>
                    Pilih Jenis Kendaraan
                  </option>
                  <option value="MOBIL">MOBIL</option>
                  <option value="MOTOR">MOTOR</option>
                </select>
              </div>
            </div>

            {/* Model */}
            <div>
              <label
                htmlFor="vehicle_model"
                className="flex text-sm font-medium leading-6"
              >
                Model
              </label>
              <div className="mt-2">
                <input
                  id="vehicle_model"
                  name="vehicle_model"
                  type="text"
                  disabled={isLoading}
                  placeholder="Masukkan Model"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Nomor Registrasi */}
            <div>
              <label
                htmlFor="nomer_registrasi"
                className="flex text-sm font-medium leading-6"
              >
                Nomor Registrasi <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <input
                  id="nomer_registrasi"
                  name="nomer_registrasi"
                  type="text"
                  disabled={isLoading}
                  value={registeredNumber}
                  placeholder="Masukkan Nomor Registrasi Kendaraan"
                  onChange={(e) => setRegisteredNumber(e.target.value)}
                  required
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Nomor BPKB */}
            <div>
              <label
                htmlFor="nomor_bpkb"
                className="flex text-sm font-medium leading-6"
              >
                Nomor BPKB <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <input
                  id="nomor_bpkb"
                  name="nomor_bpkb"
                  type="text"
                  disabled={isLoading}
                  value={bpkb}
                  placeholder="Masukkan Nomor BPKB Kendaraan"
                  onChange={(e) => setBpkb(e.target.value)}
                  required
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Nomor Mesin */}
            <div>
              <label
                htmlFor="nomor_mesin"
                className="flex text-sm font-medium leading-6"
              >
                Nomor Mesin <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <input
                  id="nomor_mesin"
                  name="nomor_mesin"
                  type="text"
                  disabled={isLoading}
                  value={machineNumber}
                  placeholder="Masukkan Nomor Mesin Kendaraan"
                  onChange={(e) => setMachineNumber(e.target.value)}
                  required
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Tahun Pembuatan */}
            <div>
              <label
                htmlFor="tahun_pembuatan"
                className="flex text-sm font-medium leading-6"
              >
                Tahun Pembuatan <p className="ml-1 text-red-700">*</p>
              </label>
              <div className="mt-2">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="" // hilangkan label bawaan
                    views={["year"]}
                    value={manufacturedYear ? dayjs(manufacturedYear) : null}
                    onChange={(newValue) => {
                      if (newValue) {
                        const isoDate = newValue
                          .utc()
                          .startOf("year")
                          .toISOString();
                        setManufacturedYear(isoDate);
                      } else {
                        setManufacturedYear("");
                      }
                    }}
                    minDate={dayjs("1990-01-01")}
                    maxDate={dayjs().endOf("year")}
                    disabled={isLoading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        id: "tahun_pembuatan",
                        name: "tahun_pembuatan",
                        error: !manufacturedYear,
                        size: "small",
                        inputProps: {
                          readOnly: true,
                          placeholder: "Isikan Tahun Pembuatan Kendaraan",
                        },
                        sx: {
                          height: "44px",
                          borderRadius: "0.75rem",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "0.75rem",
                            height: "100%",
                            paddingTop: 0,
                            paddingBottom: 0,
                            boxShadow: "0 0 0 1px #D1D5DB",
                            transition: "box-shadow 0.2s ease",
                            "&.Mui-focused": {
                              boxShadow: "0 0 0 2px #3B82F6",
                            },
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": {
                            padding: "0 0.5rem",
                            fontSize: "0.875rem",
                            fontFamily: "Poppins, sans-serif",
                            fontStyle: "normal",
                            display: "flex",
                            alignItems: "center",
                          },
                          "& input::placeholder": {
                            fontStyle: "italic",
                            color: "#6B7280",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
            </div>

            {/* Isi Silinder */}
            <div>
              <label
                htmlFor="isi_silinder"
                className="flex text-sm font-medium leading-6"
              >
                Isi Silinder
              </label>
              <div className="mt-2">
                <input
                  id="isi_silinder"
                  name="isi_silinder"
                  type="text"
                  disabled={isLoading}
                  value={cilinder}
                  placeholder="Masukkan Isi Silinder Kendaraan"
                  onChange={(e) => setCilinder(e.target.value)}
                  className="capitalize h-11 w-full rounded-xl border-0 px-2 py-1.5 ring-1 ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Tautkan Kamera */}
            {vehicleType === "MOTOR" && (
              <div>
                <label
                  htmlFor="inventory_camera_id"
                  className="flex text-sm font-medium leading-6"
                >
                  Tautkan Kamera
                </label>
                {/* Tautkan Kamera */}
                <div className="mt-2 relative w-full">
                  <select
                    id="inventory_camera_id"
                    name="inventory_camera_id"
                    // required
                    value={cameraId}
                    onChange={(e) => setCameraId(e.target.value)}
                    className={`h-11 block px-4 pr-10 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 appearance-none ${
                      cameraId === "" ? "text-gray-400 " : "text-gray-700"
                    }`}
                  >
                    <option
                      value=""
                      onChange={(e) => setCameraId(e.target.value)}
                    >
                      --Tautkan Kendaraan dengan Kamera--
                    </option>

                    {dataCamera
                      .filter(
                        (item) =>
                          item.deviceTypeName === "Body Worn Camera" &&
                          (item.inventoriKendaraanId === null ||
                            item.uuid === cameraId)
                      )
                      .map((item) => (
                        <option key={item.uuid} value={item.uuid}>
                          {item.namaPerangkat}
                        </option>
                      ))}
                  </select>

                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="w-5 h-5 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end mr-5">
            <button
              type="submit"
              className="h-11 flex w-auto justify-center items-center rounded-xl bg-btn-login hover:bg-opacity-50 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              Simpan
            </button>
          </div>
        </div>
      </form>
      <LoadingModal open={isLoading} />
    </div>
  );
}

export default UpdateVehicleInventory;
